import React from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAdminContext } from "../../../context/AdminContext";
import { createupdateUserModel } from "../../../model/adminModel";
import CustomDatePicker from "../../../components/CustomDatePicker";
import { toast } from "react-toastify";
import SearchUserName from "../../../components/adminSearchUserName";
import { USERROLEENUM } from "../../../helper/constant";

const defaultValue: createupdateUserModel = {
  ldap_uid: "",
  firstname: "",
  lastname: "",
  token: "",
  email_id: "",
  doj: null,
  designation: "",
  managetname: "",
  employee_id: 0,
  pre_experience: "",
  manager_id: null,
  role_id: 5,
  department_id: 0,
  status: 1,
};

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { id: number; formtype: string } | undefined; // Assuming state is an object with an 'id' property
  // Access 'id' from state if it exists
  const userID = state?.id;

  const {
    userDetail,
    listDepartment,
    listRole,
    getUserBYID,
    getUserupdate,
    getUserCreate,
    deleteUserByID,
    getAllRole,
    getAllDepartment,
  } = useAdminContext();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<createupdateUserModel>({ mode: "all", defaultValues: defaultValue });

  React.useEffect(() => {
    reset(defaultValue);
    if (!listRole) getAllRole({ skip: 0, limit: 50 });
    if (!listDepartment) getAllDepartment({ skip: 0, limit: 50 });
    if (state?.formtype === "update") {
      getUserBYID(Number(userID));
    }
  }, [state?.formtype, reset, getUserBYID]);

  React.useEffect(() => {
    if (userDetail && state?.formtype === "update") {
      setValue("status", userDetail.status ?? 1);
      setValue("ldap_uid", userDetail.ldap_uid);
      setValue("department_id", userDetail.department_id);
      setValue("designation", userDetail.designation);
      setValue("doj", moment(userDetail.doj).isValid() ? moment(userDetail.doj).toDate() : null);
      setValue("email_id", userDetail.email_id);
      setValue("employee_id", userDetail.employee_id);
      setValue("firstname", userDetail.firstname);
      setValue("lastname", userDetail.lastname);
      setValue("manager_id", userDetail.manager_id);
      setValue("managetname", userDetail.manager_name);
      setValue("pre_experience", String(userDetail.pre_experience));
      setValue("role_id", userDetail.role_id);
    }
  }, [userDetail, setValue]);

  const onSubmit: SubmitHandler<createupdateUserModel> = async (data) => {
    const Payload: createupdateUserModel = {
      ldap_uid: data.firstname.toLowerCase() + "." + data.lastname.toLowerCase(),
      firstname: data.firstname,
      lastname: data.lastname,
      email_id: data.email_id,
      doj: data.doj,
      designation: data.designation,
      employee_id: Number(data.employee_id),
      pre_experience: data.pre_experience ? data.pre_experience : null,
      manager_id: data.manager_id,
      role_id: data.role_id ? Number(data.role_id) : USERROLEENUM.EMPLOYEE,
      department_id: data.department_id,
      token: "",
      status: data.status,
    };
    if (state?.formtype === "update") {
      await getUserupdate(Number(userID), Payload);
    }
    if (state?.formtype === "create") {
      await getUserCreate(Payload);
    }
  };

  const handleChange = () => {
    setValue("ldap_uid", watch("firstname").toLowerCase() + "." + watch("lastname").toLowerCase());
    setValue(
      "email_id",
      watch("firstname").toLowerCase() + "." + watch("lastname").toLowerCase() + "@ionidea.com",
    );
  };

  const confirmDelete = (itemId: number) => {
    toast.info(
      <div>
        <p>Are you sure you want to delete this user?</p>
        <div className='mt-2'>
          <button className='px-4 py-1  tex-sm bg-blue-600 text-white' onClick={() => handleDelete(itemId)}>
            Yes
          </button>
          <button className='px-4 py-1 mx-2 tex-sm bg-red-500 text-white' onClick={cancelDelete}>
            No
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
      },
    );
  };

  const handleDelete = (itemId: number) => {
    // Perform delete action here
    toast.dismiss(); // Dismiss the current toast
    // console.log('itemId', itemId)
    deleteUserByID(itemId);
  };

  const cancelDelete = () => {
    toast.dismiss(); // Dismiss the current toast
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='max-w-4xl grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3'>
        <div className='mb-2 '>
          <label htmlFor='Code' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Employee Id <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='employee_id'
            control={control}
            rules={{ required: "Employee Id is required" }}
            render={({ field }) => (
              <input
                {...field}
                type='number'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.employee_id && <p className='text-red-500 text-sm mt-1'>{errors.employee_id.message}</p>}
        </div>
        <div className='mb-2 '>
          <label
            htmlFor='projectName'
            className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'
          >
            First Name <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='firstname'
            control={control}
            rules={{ required: "First Name is required" }}
            render={({ field }) => (
              <input
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange();
                }}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.firstname && <p className='text-red-500 text-sm mt-1'>{errors.firstname.message}</p>}
        </div>

        <div className='mb-2 '>
          <label htmlFor='lastname' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Last Name <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='lastname'
            control={control}
            rules={{ required: "Lastname Name is required" }}
            render={({ field }) => (
              <input
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange();
                }}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.lastname && <p className='text-red-500 text-sm mt-1'>{errors.lastname.message}</p>}
        </div>

        <div className='mb-2 '>
          <label
            htmlFor='projectName'
            className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'
          >
            ldap uid <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='ldap_uid'
            control={control}
            rules={{ required: "ldap_uid is required" }}
            render={({ field }) => (
              <input
                {...field}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.ldap_uid && <p className='text-red-500 text-sm mt-1'>{errors.ldap_uid.message}</p>}
        </div>
        <div className='mb-2 '>
          <label
            htmlFor='projectName'
            className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'
          >
            Email <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='email_id'
            control={control}
            rules={{ required: "Email is required" }}
            render={({ field }) => (
              <input
                {...field}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.email_id && <p className='text-red-500 text-sm mt-1'>{errors.email_id.message}</p>}
        </div>

        <div className='mb-2 '>
          <label htmlFor='Code' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Previous Experience
          </label>
          <Controller
            name='pre_experience'
            control={control}
            rules={{
              pattern: {
                value: /^[0-9]*\.?[0-9]+$/,
                message: "Only positive float values are allowed",
              },
            }}
            render={({ field }) => (
              <input
                {...field}
                value={field.value ?? ''}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.pre_experience && (
            <p className='text-red-500 text-sm mt-1'>{errors.pre_experience.message}</p>
          )}
        </div>

        <div className='mb-2 '>
          <label htmlFor='Code' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Designation <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='designation'
            control={control}
            rules={{ required: "Designation is required" }}
            render={({ field }) => (
              <input
                {...field}
                type='text'
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              />
            )}
          />
          {errors.designation && <p className='text-red-500 text-sm mt-1'>{errors.designation.message}</p>}
        </div>

        <div className='mb-2'>
          <label htmlFor='doj' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Date Of Joining <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='doj'
            control={control}
            rules={{ required: "Date Of Joining is required" }}
            render={({ field }) => (
              <CustomDatePicker
                onChange={field.onChange}
                selectedDate={field.value}
                isClearable={false}
                dateFormat='dd/MM/yyyy'
              />
            )}
          />
          {errors.doj && <p className='text-red-500 text-sm mt-1'>{errors.doj.message}</p>}
        </div>

        <div className='mb-2'>
          <label htmlFor='manager' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Manager <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='managetname'
            control={control}
            rules={{ required: "Manager is required" }}
            render={({ field }) => (
              <SearchUserName
                value={field.value ?? ""}
                onChange={field.onChange}
                setOptionsData={(item) => {
                  console.log(item);
                  setValue("managetname", item?.ldap_uid ?? "");
                  setValue("manager_id", item?.employee_id ?? null);
                }}
              />
            )}
          />
          {errors.manager_id && <p className='text-red-500 text-sm mt-1'>{errors.manager_id.message}</p>}
        </div>

        <div className='mb-2'>
          <label htmlFor='status' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Role <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='role_id'
            control={control}
            rules={{ required: "Project type is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                {/* <option selected>Select</option> */}
                {listRole &&
                  listRole.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.role}
                    </option>
                  ))}
              </select>
            )}
          />
          {errors.role_id && <p className='text-red-500 text-sm mt-1'>{errors.role_id.message}</p>}
        </div>

        <div className='mb-2'>
          <label
            htmlFor='department'
            className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'
          >
            Department <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='department_id'
            control={control}
            rules={{ required: "Department is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                <option selected>Select</option>
                {listDepartment &&
                  listDepartment.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.department}
                    </option>
                  ))}
              </select>
            )}
          />
          {errors.department_id && (
            <p className='text-red-500 text-sm mt-1'>{errors.department_id.message}</p>
          )}
        </div>
        <div className='mb-2'>
          <label htmlFor='status' className='block mb-2 text-sm font-medium text-gray-900 dark:text-white'>
            Status <span className='text-red-400'>*</span>
          </label>
          <Controller
            name='status'
            control={control}
            rules={{ required: "Project type is required" }}
            render={({ field }) => (
              <select
                value={field.value}
                onChange={field.onChange}
                className='shadow-sm bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 dark:shadow-sm-light'
              >
                <option value={1}>Active</option>
                <option selected value={0}>Inactive</option>
              </select>
            )}
          />
          {errors.role_id && <p className='text-red-500 text-sm mt-1'>{errors.role_id.message}</p>}
        </div>
      </div>
      <div className='max-w-4xl flex justify-between mt-4'>
        {state?.formtype === "update" && (
          <button
            type='button'
            onClick={() => confirmDelete(Number(userID))}
            className='text-red-600 bg-white border border-red-600 hover:text-white hover:bg-red-600 focus:ring-4  focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-gray-600 dark:hover:bg-red-600 dark:focus:ring-red-600'
          >
            Delete user
          </button>
        )}

        <div>
          <button
            type='button'
            onClick={() => navigate(-1)}
            className='text-white bg-red-600 hover:bg-red-800 focus:ring-4 mr-2 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800'
          >
            Close
          </button>
          <button
            type='submit'
            className='text-white bg-blue-700 mr-2 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800'
          >
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
